<template>
    <div class="vacation-control-container" :style="backgroundStyles">
        <!-- Header moderno con gradiente -->
        <div class="modern-header" :style="headerStyle">
            <div class="header-content">
                <div class="header-left">
                    <v-icon size="32" color="white" class="mr-3">mdi-calendar-check</v-icon>
                    <h2 class="header-title">Control de Vacaciones</h2>
                </div>
                <div class="header-right">                    
                    <!-- Botones de acción -->
                    <v-btn 
                        v-if="!edit && selected.length > 0" 
                        @click="editFunc()"
                        color="warning"
                        class="ml-2"
                    >
                        <v-icon left>mdi-pencil</v-icon>
                        Editar
                    </v-btn>
                    <v-btn 
                        v-if="selected.length > 0" 
                        @click="removedFunc(selectedData)"
                        color="error"
                        class="ml-2"
                    >
                        <v-icon left>mdi-delete</v-icon>
                        Eliminar
                    </v-btn>
                    <v-btn 
                        v-if="edit" 
                        @click="saveFunc(selectedData)"
                        color="success"
                        class="ml-2"
                    >
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar
                    </v-btn>
                    <v-btn 
                        v-if="edit" 
                        @click="exitFunc()"
                        color="white"
                        class="ml-2"
                    >
                        <v-icon left>mdi-close</v-icon>
                        Cancelar
                    </v-btn>
                    <!-- Selector de tema -->
                    <v-menu offset-y style="z-index: 50;">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                icon
                                color="white"
                                v-bind="attrs"
                                v-on="on"
                                class="theme-btn ml-2"
                            >
                                <v-icon>mdi-palette</v-icon>
                            </v-btn>
                        </template>
                        <v-card class="theme-selector">
                            <v-card-title class="pb-4">
                                <v-icon class="mr-2">mdi-palette</v-icon>
                                Seleccionar Tema
                            </v-card-title>
                            <v-card-text class="pt-0">
                                <v-row>
                                    <v-col 
                                        v-for="theme in themeOptions" 
                                        :key="theme.name"
                                        cols="6"
                                        class="pa-1"
                                    >
                                        <v-card
                                            :class="['theme-card', { 'selected': selectedTheme.name === theme.name }]"
                                            @click="changeTheme(theme)"
                                            elevation="2"
                                        >
                                            <div class="theme-preview" :style="{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})` }">
                                                <div class="theme-name">{{ theme.displayName }}</div>
                                            </div>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-menu>
                </div>
            </div>
        </div>

        <!-- Contenido principal -->
        <div class="main-content">
            <!-- Filtros mejorados -->
            <v-card v-if="!edit" class="filter-card" elevation="3">
                <v-card-title class="filter-title">
                    <v-icon class="mr-2" :color="selectedTheme.primary">mdi-filter</v-icon>
                    Filtros de Búsqueda
                </v-card-title>
                <v-card-text class="mt-4">
                    <!-- Fila 1: Búsqueda principal -->
                    <v-row>
                        <v-col cols="12" sm="6" md="5">
                            <s-text
                                label="Nombre o DNI"
                                v-model="filter.PrsDocumentNumber"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                                prepend-inner-icon="mdi-account-search"
                                clearable
                                @keyupEnter="pagination()"
                            ></s-text>
                        </v-col>
                        <v-col cols="12" sm="6" md="5">
                            <s-select-definition
                                :def="1060"
                                label="Planilla"
                                multiple
                                v-model="filter.TypePayrollsValue"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="2" class="d-flex align-end">
                            <v-btn
                                :color="selectedTheme.primary"
                                @click="pagination()"
                                small
                                dark
                                block
                                style="margin-bottom: 4px;"
                            >
                                <v-icon left>mdi-magnify</v-icon>
                                Buscar
                            </v-btn>
                        </v-col>
                    </v-row>
                    <!-- Fila 2: Filtros de período -->
                    <v-row>
                        <v-col cols="12" sm="6" md="3">
                            <v-select
                                :items="years"
                                label="Año"
                                v-model="filter.Year"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                            <s-select-definition
                                :def="1537"
                                label="Sub Periodo"
                                v-model="filter.PdsType"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                            <s-periods
                                label="Semana/Mes"
                                v-model="filter.VccSemOMonth"
                                itemvalue="PdsNum"
                                :type="filter.PdsType"
                                :year="filter.Year"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="3">
                            <s-date
                                label="Fecha de Pago"
                                v-model="filter.VccDatePay"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Formulario de edición mejorado -->
            <v-card v-else class="edit-card" elevation="3">
                <v-card-title class="edit-title">
                    <v-icon class="mr-2" :color="selectedTheme.primary">mdi-pencil</v-icon>
                    Editar Registro de Vacaciones
                </v-card-title>
                <v-card-text class="mt-4">
                    <v-row>
                        <v-col cols="12" md="3">
                            <s-text
                                label="N° PAPELETA"
                                v-model="selectedData.VccNumBallot"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                                prepend-inner-icon="mdi-file-document-outline"
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <s-select-definition
                                :def="1568"
                                label="Estado"
                                v-model="selectedData.VccStatus"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <s-text
                                label="Importe Pagado"
                                v-model="selectedData.VccAmountPaid"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                                decimal
                                :min="0"
                                :maxDecimal="2"
                                prepend-inner-icon="mdi-piggy-bank"
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <s-date 
                                label="Fecha de Pago" 
                                v-model="selectedData.VccDatePay"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12">
                            <s-text
                                label="Observación"
                                v-model="selectedData.VccObservation"
                                :color="selectedTheme.primary"
                                outlined
                                dense
                                prepend-inner-icon="mdi-note-text"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Tabla de datos mejorada -->
            <v-card class="data-table-card" elevation="3">
                <v-card-title class="table-title">
                    <v-icon class="mr-2" :color="selectedTheme.primary">mdi-table</v-icon>
                    Registros de Vacaciones
                    <v-spacer></v-spacer>
                    <v-chip 
                        v-if="selected.length > 0" 
                        :color="selectedTheme.accent"
                        text-color="white"
                        small
                    >
                        {{ selected.length }} seleccionado(s)
                    </v-chip>
                    <v-btn 
                        @click="exportToExcel"
                        color="success"
                        :loading="exporting"
                        small
                        class="ml-3"
                        >
                            <v-icon>mdi-microsoft-excel</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="pa-0">
                    <v-data-table
                        :headers="headers"
                        item-key="VccID"
                        v-model="selected"
                        @click:row="selectedCheckSlot($event)"
                        :items="items"
                        :items-per-page="25"
                        :footer-props="{
                            showCurrentPage: true,
                            showFirstLastPage: true,
                            itemsPerPageOptions: [25, 50, 100, -1],
                        }"
                        class="modern-table"
                    >
                        <template v-slot:item.VccDateInit="{ item }">
                            <v-chip small outlined :color="selectedTheme.info">
                                {{ $fun.formatDateView(item.VccDateInit) }}
                            </v-chip>
                        </template>

                        <template v-slot:item.VccDateEnd="{ item }">
                            <v-chip small outlined :color="selectedTheme.info">
                                {{ $fun.formatDateView(item.VccDateEnd) }}
                            </v-chip>
                        </template>

                        <template v-slot:item.VccDatePay="{ item }">
                            <v-chip small outlined :color="selectedTheme.secondary">
                                {{ $fun.formatDateView(item.VccDatePay) }}
                            </v-chip>
                        </template>

                        <template v-slot:item.VccStatusName="{ item }">
                            <v-chip 
                                small 
                                :color="getStatusColor(item.VccStatus)"
                                text-color="white"
                            >
                                <v-icon left small>{{ getStatusIcon(item.VccStatus) }}</v-icon>
                                {{ item.VccStatusName }}
                            </v-chip>
                        </template>

                        <template v-slot:item.VccMethodName="{ item }">
                            <v-chip 
                                small 
                                :color="getMethodColor(item.VccMethod)"
                                text-color="white"
                            >
                                <!-- <v-icon left small>{{ getMethodIcon(item.VccMethod) }}</v-icon> -->
                                {{ item.VccMethodName }}
                            </v-chip>
                        </template>

                        <template v-slot:item.VccAmountPaid="{ item }">
                            <v-chip small :color="selectedTheme.success" text-color="white">
                                {{ item.VccAmountPaid | currencyPEN }}
                            </v-chip>
                        </template>

                        <template v-slot:item.VccDays="{ item }">
                            <v-chip small :color="selectedTheme.primary" text-color="white">
                                {{ item.VccDays }} días
                            </v-chip>
                        </template>

                        <template v-slot:item.VctSoldDays="{ item }">
                            <v-chip v-if="item.VctSoldDays > 0" small color="amber darken-2" text-color="white">
                                <v-icon left small>mdi-cash</v-icon>
                                {{ item.VctSoldDays }}d vend.
                            </v-chip>
                            <span v-else class="grey--text">-</span>
                        </template>

                        <template v-slot:item.VccSemOMonth="{ item }">
                            {{ payrollMonthly.includes(item.TypePayroll) ? getMonth(item.VccSemOMonth) : item.VccSemOMonth }}
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </div>
    </div>
</template>

<script>
import SPeriods from "../../../components/Utils/SPeriods.vue";
import sToolbarPerson from "@/components/Utils/Person/sToolbarPerson";
import HmnVacationsControlService from '@/services/HumanResource/HmnVacationsControlService';
import { exportVuetifyToExcel } from '@/utils/excelExport';

export default {
    components: {
        sToolbarPerson,
        SPeriods
    },
    data() {
        return {
            payrollMonthly: [1, 4, 7, 8],
            payrollWeekly: [2, 5, 11],
            items: [],
            edit: false,
            selected: [],
            selectedData: {},
            exporting: false,
            filter: {
                PrsDocumentNumber: null,
                TypePayrollsValue: [],
                Year: new Date().getFullYear(),
                PdsType: 1,
                VccSemOMonth: null,
                VccDatePay: null
            },
            headers: [
                // { text: "ID", value: "VccID", align: "center", width: 70 },
                { text: "N° PAPELETA", value: "VccNumBallot", width: 120, align: "center" },
                { text: "DNI", value: "PrsDocumentNumber", width: 100 },
                { text: "Nombre", value: "NtpFullName", width: 250 },
                { text: "Periodo", value: "VctPeriod", width: 100, align: "center" },
                { text: "Inicio", value: "VccDateInit", width: 120, align: "center" },
                { text: "Fin", value: "VccDateEnd", width: 120, align: "center" },
                { text: "Días", value: "VccDays", width: 80, align: "center" },
                { text: "Días Vendidos", value: "VctSoldDays", width: 110, align: "center" },
                { text: "Planilla", value: "TypePayrollName", width: 130, align: "center" },
                { text: "Mes/Semana", value: "VccSemOMonth", width: 100, align: "center" },
                { text: "Modalidad", value: "VccMethodName", width: 130, align: "center" },
                { text: "Fecha Pago", value: "VccDatePay", width: 120, align: "center" },
                { text: "Estado", value: "VccStatusName", width: 120, align: "center" },
                { text: "Importe (Soles)", value: "VccAmountPaid", width: 100, align: "center" },
                { text: "Observaciones", value: "VccObservation", width: 200 },
            ],
            themeOptions: [
                {
                    name: 'blue',
                    displayName: 'Azul Corporativo',
                    primary: '#1976D2',
                    secondary: '#42A5F5',
                    accent: '#FFC107',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#2196F3'
                },
                {
                    name: 'purple',
                    displayName: 'Púrpura Moderno',
                    primary: '#7B1FA2',
                    secondary: '#AB47BC',
                    accent: '#E91E63',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#9C27B0'
                },
                {
                    name: 'green',
                    displayName: 'Verde Natural',
                    primary: '#388E3C',
                    secondary: '#66BB6A',
                    accent: '#FF5722',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#009688'
                },
                {
                    name: 'orange',
                    displayName: 'Naranja Energético',
                    primary: '#F57C00',
                    secondary: '#FFB74D',
                    accent: '#E91E63',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#FF9800'
                },
                {
                    name: 'dark',
                    displayName: 'Oscuro Elegante',
                    primary: '#424242',
                    secondary: '#616161',
                    accent: '#00BCD4',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#009688'
                },
                {
                    name: 'pinkSoft',
                    displayName: 'Rosado Pastel',
                    primary: '#F48FB1',
                    secondary: '#FCE4EC',
                    accent: '#EC407A',
                    success: '#4CAF50',
                    warning: '#FF9800',
                    error: '#F44336',
                    info: '#F48FB1'
                }
            ],
            selectedTheme: {}
        }
    },
    computed: {
        years() {
            const currentYear = new Date().getFullYear();
            const startYear = 2020;
            const yearsList = [];
            for (let y = currentYear + 1; y >= startYear; y--) {
                yearsList.push(y);
            }
            return yearsList;
        },
        headerStyle() {
            return {
                background: `linear-gradient(135deg, ${this.selectedTheme.primary}, ${this.selectedTheme.secondary})`,
                boxShadow: `0 4px 20px ${this.selectedTheme.primary}30`
            }
        },
        backgroundStyles() {
            return {
                background: `linear-gradient(135deg, #f5f7fa 0%, ${this.selectedTheme.secondary}40 100%)`
            };
        }
    },
    methods: {
        async exportToExcel() {
            this.exporting = true;
            
            try {
                await exportVuetifyToExcel(
                this.headers,
                this.items,
                {
                    title: 'Reporte de Control de Vacaciones',
                    subtitle: `Generado el ${this.$fun.getDateTimeShortDB()}`,
                    filename: `Reporte_control_vacaciones ${this.$fun.getDateTimeShortDB()}`,
                }
                );
            } catch (error) {
                console.error('Error al exportar a Excel:', error);
            } finally {
                this.exporting = false;
            }
        },
        getMonth(month) {
            const months = [
                'Enero', 'Febrero', 'Marzo', 'Abril', 
                'Mayo', 'Junio', 'Julio', 'Agosto',
                'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
            ];
            
            const monthIndex = Math.max(0, Math.min(11, month - 1));
            
            return months[monthIndex];
        },
        changeTheme(theme) {
            this.selectedTheme = theme;
            localStorage.setItem('vacationControlTheme', JSON.stringify(theme));
        },
        loadTheme() {
            const saved = localStorage.getItem('vacationControlTheme');
            if (saved) {
                this.selectedTheme = JSON.parse(saved);
            } else {
                this.selectedTheme = this.themeOptions[0]; // Tema por defecto
            }
        },
        getStatusColor(status) {
            switch(status) {
                case 1: return this.selectedTheme.warning;
                case 2: return this.selectedTheme.success;
                default: return this.selectedTheme.info;
            }
        },
        getStatusIcon(status) {
            switch(status) {
                case 1: return 'mdi-clock-outline';
                case 2: return 'mdi-check-circle';
                default: return 'mdi-information';
            }
        },
        getMethodColor(method) {
            switch(method) {
                case 1: return this.selectedTheme.error;
                case 2: return this.selectedTheme.success;
                default: return this.selectedTheme.info;
            }
        },
        getMethodIcon(method) {
            switch(method) {
                case 1: return 'mdi-close-circle';
                case 2: return 'mdi-check-circle';
                default: return 'mdi-help-circle';
            }
        },
        saveFunc(item) {
            console.log(item);
            item.UsrCreateID = this.$fun.getUserID();
            item.UsrUpdateID = this.$fun.getUserID();

            HmnVacationsControlService.save(item, this.$fun.getUserID()).then((res) => {
                if (res.status == 200) {
                    this.$fun.alert("Actualizado Correctamente", "success");
                    this.pagination();
                    this.edit = false;
                }
            })
        },
        editFunc() {
            this.edit = true;
        },
        exitFunc() {
            this.edit = false;
        },
        removedFunc(item) {
            console.log(item);
            item.UsrCreateID = this.$fun.getUserID();
            item.UsrUpdateID = this.$fun.getUserID();
            item.SecStatus = 0;

            this.$fun.alert("¿Seguro de eliminar?", "question")
            .then(r => {
                if(r.value) {
                    HmnVacationsControlService.save(item, this.$fun.getUserID()).then((res) => {
                        if (res.status == 200) {
                            this.$fun.alert("Eliminado Correctamente", "success");
                            this.pagination();
                        }
                    })
                }
            })
        },
        selectedCheckSlot(obj) {
            console.log(obj);
            if (obj !== null) {
                this.selected = [];
                this.selected.push(obj);
                this.selectedData = { ...this.selected[0] };
            }
        },
        pagination() {
            let TypePayrolls = "";
            if (this.filter.TypePayrollsValue) {
                if (Array.isArray(this.filter.TypePayrollsValue)) {
                    TypePayrolls = this.filter.TypePayrollsValue.join(',');
                } else {
                    try {
                        const parsed = JSON.parse(this.filter.TypePayrollsValue);
                        if (Array.isArray(parsed)) {
                            TypePayrolls = parsed.join(',');
                        } else {
                            TypePayrolls = this.filter.TypePayrollsValue;
                        }
                    } catch (e) {
                        TypePayrolls = this.filter.TypePayrollsValue;
                    }
                }
            }

            const payload = {
                ...this.filter,
                TypePayrolls: TypePayrolls
            };

            HmnVacationsControlService.pagination(payload, this.$fun.getUserID()).then((r) => {
                if (r.status === 200) {
                    console.log(r);
                    this.items = r.data;
                }
            });
        }
    },
    watch: {
        'filter.VccSemOMonth'() {
            console.log(this.filter);
        },
    },
    created() {
        this.loadTheme();
        this.pagination();
    }
}
</script>

<style scoped>
.vacation-control-container {
    min-height: 100vh;
    /* background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); */
    padding: 0;
    border-radius: 10px;
}

.modern-header {
    padding: 20px 30px;
    margin-bottom: 25px;
    border-radius: 10px;
    position: relative;
    overflow: hidden;
}

.modern-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    pointer-events: none;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 1;
}

.header-left {
    display: flex;
    align-items: center;
}

.header-title {
    color: white;
    font-weight: 300;
    font-size: 28px;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.header-right {
    display: flex;
    align-items: center;
}

.theme-btn {
    background: rgba(255,255,255,0.2) !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.3);
}

.theme-selector {
    min-width: 300px;
    border-radius: 15px;
}

.theme-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 10px;
    overflow: hidden;
    border: 2px solid transparent;
}

.theme-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.theme-card.selected {
    border-color: #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2);
}

.theme-preview {
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 500;
    font-size: 12px;
    text-shadow: 0 1px 2px rgba(0,0,0,0.3);
}

.main-content {
    padding: 0 30px 30px;
}

.filter-card, .edit-card, .data-table-card {
    border-radius: 15px;
    margin-bottom: 25px;
    transition: all 0.3s ease;
}

.filter-card:hover, .edit-card:hover, .data-table-card:hover {
    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.filter-title, .edit-title, .table-title {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    border-radius: 15px 15px 0 0;
    font-weight: 600;
    font-size: 18px;
    color: #495057;
    padding: 20px 24px;
    margin: 0;
}

.modern-table {
    border-radius: 0 0 15px 15px;
    overflow: hidden;
}

.modern-table >>> .v-data-table__wrapper {
    border-radius: 0 0 15px 15px;
}

.modern-table >>> .v-data-table-header {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
}

.modern-table >>> .v-data-table-header th {
    font-weight: 600;
    color: #495057;
    border-bottom: 2px solid #dee2e6;
}

.modern-table >>> tbody tr {
    transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
    background: rgba(0,0,0,0.04);
    transform: scale(1.001);
}

.modern-table >>> tbody tr:nth-child(even) {
    background: rgba(0,0,0,0.02);
}

.modern-table >>> tbody tr.v-data-table__selected {
    background: rgba(25, 118, 210, 0.1) !important;
    border-left: 4px solid #1976D2;
}

/* Animaciones */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.filter-card, .edit-card, .data-table-card {
    animation: fadeIn 0.6s ease;
}

/* Responsive */
@media (max-width: 768px) {
    .modern-header {
        padding: 15px 20px;
    }
    
    .header-title {
        font-size: 22px;
    }
    
    .main-content {
        padding: 0 20px 20px;
    }
    
    .header-content {
        flex-direction: column;
        gap: 15px;
    }
    
    .header-right {
        width: 100%;
        justify-content: center;
    }
}
</style>